import {
  WELL_COLS,
  WELL_ROWS,
  idx,
  idxCol,
  idxRow,
  type CellIndex,
  type GemKind,
  type Grid,
  type Piece,
} from './types';
import { restRow, setCell } from './grid';

export type Rng = () => number;

/** mulberry32 — small, fast, deterministic. Used for run seeding and tests. */
export function seededRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * What an enemy will do on its next action. Every intent is telegraphed a full
 * turn ahead — including the board-manipulation ones, which is the whole point.
 * Stone drops and hexes are the real damage; the attack numbers are the part
 * the player can see coming.
 */
export type Intent =
  | { readonly kind: 'attack'; readonly amount: number }
  | { readonly kind: 'attackTimes'; readonly amount: number; readonly hits: number }
  | { readonly kind: 'block'; readonly amount: number }
  | { readonly kind: 'addStone'; readonly count: number }
  | { readonly kind: 'pushUpStone'; readonly rows: number }
  | { readonly kind: 'hexColumn' }
  | { readonly kind: 'scramble'; readonly count: number }
  | { readonly kind: 'dropPiece'; readonly piece: Piece }
  | { readonly kind: 'multi'; readonly intents: readonly Intent[] };

const SINGLE: readonly (readonly [number, number])[] = [[0, 0]];

function pick<T>(items: readonly T[], rng: Rng): T | undefined {
  if (items.length === 0) return undefined;
  return items[Math.floor(rng() * items.length)];
}

/** Fisher-Yates. Returns the same array for chaining. */
export function shuffleInPlace<T>(items: T[], rng: Rng): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = items[i];
    const b = items[j];
    if (a !== undefined && b !== undefined) {
      items[i] = b;
      items[j] = a;
    }
  }
  return items;
}

export function openColumns(grid: Grid): number[] {
  const out: number[] = [];
  for (let c = 0; c < WELL_COLS; c++) {
    if (grid[idx(0, c)] === null) out.push(c);
  }
  return out;
}

export function occupiedCells(grid: Grid): CellIndex[] {
  const out: CellIndex[] = [];
  for (let i = 0; i < grid.length; i++) if (grid[i] !== null) out.push(i);
  return out;
}

/** Cells a scramble may touch. Stone is inert — it never changes color. */
export function scrambleableCells(grid: Grid): CellIndex[] {
  const out: CellIndex[] = [];
  for (let i = 0; i < grid.length; i++) {
    const cell = grid[i] ?? null;
    if (cell !== null && cell !== 'stone') out.push(i);
  }
  return out;
}

/**
 * Drop `count` Stone gems into random open columns. They fall under gravity
 * like anything else. Returns the cells actually filled — a packed well eats
 * the rest of the drop, which is why a golem on a full board is toothless and
 * a golem on an empty board is a disaster.
 */
export function dropStones(grid: Grid, count: number, rng: Rng): CellIndex[] {
  const placed: CellIndex[] = [];
  for (let n = 0; n < count; n++) {
    const col = pick(openColumns(grid), rng);
    if (col === undefined) break;
    const row = restRow(grid, SINGLE, col);
    if (row === null) break;
    setCell(grid, row, col, 'stone');
    placed.push(idx(row, col));
  }
  return placed;
}

/**
 * Permute the colors of up to `count` random non-Stone gems among themselves.
 * Counts are conserved — this is not a reroll. That distinction matters:
 * scrambling a board full of Ember leaves it full of Ember, just arranged
 * wrong, so the player's outs survive even when their setup does not.
 */
export function scrambleGems(grid: Grid, count: number, rng: Rng): CellIndex[] {
  const cells = scrambleableCells(grid);
  shuffleInPlace(cells, rng);
  const chosen = cells.slice(0, Math.min(count, cells.length));
  if (chosen.length < 2) return chosen;

  const kinds: GemKind[] = [];
  for (const i of chosen) {
    const cell = grid[i] ?? null;
    if (cell !== null) kinds.push(cell);
  }
  shuffleInPlace(kinds, rng);
  chosen.forEach((cellIdx, n) => {
    const kind = kinds[n];
    if (kind !== undefined) grid[cellIdx] = kind;
  });
  return chosen;
}

/** Turn every gem in a random non-empty column to Stone. Returns the column. */
export function hexRandomColumn(grid: Grid, rng: Rng): number | null {
  const candidates: number[] = [];
  for (let c = 0; c < WELL_COLS; c++) {
    for (let r = 0; r < WELL_ROWS; r++) {
      if (grid[idx(r, c)] !== null) {
        candidates.push(c);
        break;
      }
    }
  }
  const col = pick(candidates, rng);
  if (col === undefined) return null;
  for (let r = 0; r < WELL_ROWS; r++) grid[idx(r, col)] = 'stone';
  return col;
}

export { idxCol, idxRow };
